import { getCurrencyFormatterFromCurrency } from '@repo/utils'
import { Locale } from '@repo/utils'
import { useIntl } from 'react-intl'

import { divideByFactor, divideByFactorRate, multiplyByFactor } from './prices'
import { VAT_STATUS } from '../components/contexts/price-context/VAT_STATUS'
import { CurrencyEnum, ServiceProvisionUnitEnum } from '../lib/graphql'

export const DEFAULT_MINIMUM_VALUE_IN_CENTS = multiplyByFactor(5)

export const getFormatter = ({
  currency,
  minimumFractionDigits,
  maximumFractionDigits,
}: {
  currency?: CurrencyEnum
  minimumFractionDigits?: number
  maximumFractionDigits?: number
}) => {
  return new Intl.NumberFormat(getCurrencyFormatterFromCurrency(currency), {
    style: 'currency',
    currencyDisplay: 'narrowSymbol',
    currency: currency ?? CurrencyEnum.EUR,
    minimumFractionDigits: minimumFractionDigits ?? 0,
    maximumFractionDigits: maximumFractionDigits ?? 0,
  })
}

const decimalPrice = (
  amount: number | undefined | null,
  {
    vatStatus,
    currency,
    minimumValueInCents,
  }: {
    vatStatus?: VAT_STATUS
    currency?: CurrencyEnum
    minimumValueInCents?: number
  } = {},
) => {
  const formatter = getFormatter({
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  if (
    amount === undefined ||
    amount === null ||
    (minimumValueInCents !== undefined && amount < minimumValueInCents)
  ) {
    return '-'
  }

  // Format the amount
  let formattedPrice = formatter.format(divideByFactor(amount))

  // Replace non-breaking spaces and normalize output (those non-breaking spaces are not displayed correctly in some cases)
  formattedPrice = formattedPrice
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  return `${formattedPrice}${vatStatus ? ` ${vatStatus}` : ''}`
}

const compactPrice = (amountStr: string): string => {
  const amount = parseFloat(amountStr.replace(/[^0-9.-]+/g, ''))

  if (amount >= 1e6) {
    return `${Number((amount / 1e6).toFixed(1))}M`
  }
  if (amount >= 1e3) {
    return `${Number((amount / 1e3).toFixed(1))}K`
  }
  return amountStr
}

const roundedPrice = (
  amount: number | undefined | null,
  {
    currency,
    minimumValueInCents,
    unit,
    isCompact,
  }: {
    currency?: CurrencyEnum
    minimumValueInCents?: number
    unit?: ServiceProvisionUnitEnum
    isCompact?: boolean
  } = {},
) => {
  const roundedFormatter = getFormatter({ currency })

  if (
    amount === undefined ||
    amount === null ||
    (minimumValueInCents !== undefined &&
      minimumValueInCents !== null &&
      amount < minimumValueInCents)
  ) {
    return '-'
  }

  let amountStr = roundedFormatter.format(divideByFactor(amount))
  if (isCompact) {
    amountStr = compactPrice(amountStr)
  }
  return `${amountStr} ${unit ? getPriceUnitSuffix({ unit }) : ''}`
}

const percentage = ({
  rate,
  locale,
}: {
  rate: number | undefined | null
  locale: Locale
}) => {
  if (rate === undefined || rate === null) {
    return '-'
  }
  return divideByFactorRate(rate).toLocaleString(locale, {
    style: 'percent',
    minimumFractionDigits: 2,
  })
}

const getPriceUnitSuffix = ({ unit }: { unit: ServiceProvisionUnitEnum }) => {
  const intl = useIntl()

  return unit === ServiceProvisionUnitEnum.INDIVIDUAL
    ? intl.formatMessage({
        id: 'ui.currencyFormatter.perPerson',
        defaultMessage: 'par personne',
      })
    : intl.formatMessage({
        id: 'ui.currencyFormatter.perService',
        defaultMessage: 'par prestation',
      })
}

export { decimalPrice, percentage, roundedPrice }
